/**
 * Social Authentication Utilities for Cognito OAuth
 *
 * Handles Google Workspace and Microsoft 365 B2B authentication
 * via Cognito Hosted UI redirects.
 */

export type SocialProvider = "Google" | "Microsoft";

interface CognitoOAuthConfig {
  domain: string;
  clientId: string;
  redirectUri: string;
  responseType: string;
  scope: string;
}

const getCognitoConfig = (): CognitoOAuthConfig => {
  const stage = import.meta.env.VITE_STAGE || "dev";

  const domainMap: Record<string, string> = {
    dev: "sky-dev",
    staging: "sky-staging",
    prod: "sky-prod",
    production: "sky-prod",
  };

  const regionMap: Record<string, string> = {
    dev: "eu-west-1",
    staging: "ca-central-1",
    prod: "ca-central-1",
    production: "ca-central-1",
  };

  const domain = domainMap[stage] || "sky-dev";
  const region = regionMap[stage] || "ca-central-1";

  return {
    domain: `${domain}.auth.${region}.amazoncognito.com`,
    clientId: import.meta.env.VITE_COGNITO_SOCIAL_CLIENT_ID || "",
    redirectUri: `${window.location.origin}/auth/callback`,
    responseType: "code",
    scope: "openid email profile",
  };
};

const SOCIAL_AUTH_PROVIDER_KEY = "social_auth_provider";
const SOCIAL_AUTH_LINK_RETRY_KEY = "social_auth_link_retry";

/**
 * Initiates social login by redirecting to Cognito Hosted UI
 */
export const initiateOAuthLogin = (provider: SocialProvider): void => {
  const config = getCognitoConfig();

  if (!config.clientId) {
    throw new Error("Social login is not configured for this environment");
  }

  sessionStorage.setItem(SOCIAL_AUTH_PROVIDER_KEY, provider);

  const params = new URLSearchParams({
    response_type: config.responseType,
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope,
    identity_provider: provider,
    prompt: "login",
  });

  const authUrl = `https://${config.domain}/oauth2/authorize?${params.toString()}`;
  window.location.href = authUrl;
};

/**
 * After a successful account link, re-initiates OAuth with the same provider.
 */
export const retryOAuthAfterLinking = (): boolean => {
  const alreadyRetried = sessionStorage.getItem(SOCIAL_AUTH_LINK_RETRY_KEY);
  if (alreadyRetried) {
    sessionStorage.removeItem(SOCIAL_AUTH_LINK_RETRY_KEY);
    sessionStorage.removeItem(SOCIAL_AUTH_PROVIDER_KEY);
    return false;
  }

  const provider = sessionStorage.getItem(
    SOCIAL_AUTH_PROVIDER_KEY
  ) as SocialProvider | null;
  if (!provider) {
    return false;
  }

  sessionStorage.setItem(SOCIAL_AUTH_LINK_RETRY_KEY, "true");
  initiateOAuthLogin(provider);
  return true;
};

/**
 * Clears social-auth session flags. Call after a successful login.
 */
export const clearSocialAuthFlags = (): void => {
  sessionStorage.removeItem(SOCIAL_AUTH_LINK_RETRY_KEY);
  sessionStorage.removeItem(SOCIAL_AUTH_PROVIDER_KEY);
};

/**
 * Parses OAuth callback parameters from URL
 */
export const parseOAuthCallback = (): {
  code?: string;
  error?: string;
  errorDescription?: string;
} => {
  const params = new URLSearchParams(window.location.search);

  return {
    code: params.get("code") || undefined,
    error: params.get("error") || undefined,
    errorDescription: params.get("error_description") || undefined,
  };
};

/**
 * Exchanges authorization code for tokens
 */
export const exchangeCodeForTokens = async (
  code: string
): Promise<{
  accessToken: string;
  idToken: string;
  refreshToken: string;
}> => {
  const config = getCognitoConfig();

  const response = await fetch(`https://${config.domain}/oauth2/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: config.clientId,
      code,
      redirect_uri: config.redirectUri,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error_description || "Token exchange failed");
  }

  const data = await response.json();

  return {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: data.refresh_token,
  };
};

/**
 * Error codes returned by the social registration endpoint
 */
export const SOCIAL_AUTH_ERROR_CODES = {
  PUBLIC_DOMAIN_RESTRICTED: "PUBLIC_DOMAIN_RESTRICTED",
  REGISTRATION_INCOMPLETE: "REGISTRATION_INCOMPLETE",
  REGISTRATION_COMPLETE: "REGISTRATION_COMPLETE",
  ALREADY_REGISTERED: "ALREADY_REGISTERED",
} as const;

/**
 * Calendly URL for booking a demo (used when consumer domains are blocked)
 */
export const CALENDLY_DEMO_URL = "https://calendly.com/sky-ai-demo";

/**
 * Message shown when account linking succeeds
 */
export const LINK_SUCCESS_MESSAGE =
  'Account recognized and linked. Please click "Sign in" one last time to access your account.';
